import React, { useEffect } from 'react'
import { useLocalStorage } from '../LocalStorageProvider'
import {
  getGetAllCategoriesInLocation,
  getGetSimilarProducts,
} from '../../lib/sanity.client'
import { getAlternates, replaceWildcards } from '../../lib/helper/helper'
import { BookProduct } from '../../components/product/BookProduct'
import { Description } from '../../components/shared/Description'
import { ProductDetails } from '../../components/product/ProductDetails'
import { BookButton } from './BookButton'
import Reviews from '../shared/Reviews'
import { Widget } from '../shared/Widget'
import PageHead from '../shared/PageHead'
import TileSlider from '../structure/TileSlider'
import { Highlights } from './Highlights'
import { IdealImage } from '../shared/IdealImage'
import { Route } from '../shared/Route'

interface Props {
  product: Product
}

export function Product({ product }: Props) {
  const {
    addLastViewedProduct,
    // bookmarkedProductIds,
    // removeBookmark,
    // addBookmark,
  } = useLocalStorage()
  // const isMobile = useIsMobile()
  // const [isHovered, setIsHovered] = useState(false)
  const {
    _id,
    description_1,
    description_2,
    description_3,
    route,
    image,
    h1: h1WithWildcards,
    product_link,
    price,
    highlights,
    booking_engine,
    partner,
    location,
    reviews,
    category,
    meta_title,
    meta_description,
    partner_product_id,
    name_description,
  } = product

  const h1 = h1WithWildcards?.replace('[city]', location?.name || '')
  product.h1 = h1

  const {
    name: locationName,
    country,
    cover_image: locationCoverImage,
    product_link: locationProductLink,
    _id: locationId,
  } = location
  const { stars, numberOfReviews } = reviews || {}
  const { name: nameCountry } = country

  useEffect(() => {
    addLastViewedProduct(product._id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // const isBookmarked = bookmarkedProductIds.includes(_id)

  // const renderBookmarkIcon = () => {
  //   if (isBookmarked) {
  //     const heartClass =
  //       isHovered || isMobile
  //         ? 'far fa-heart-circle-xmark'
  //         : 'fas fa-heart-circle-check'
  //     const heartColor = isHovered || isMobile ? '#858995' : '#ff4e4e'
  //     return (
  //       <span
  //         className={`${heartClass} absolute xs:top-3 xs:right-3 top-1 right-1 text-3xl`}
  //         style={{ color: heartColor }}
  //       />
  //     )
  //   } else {
  //     return (
  //       <span
  //         className="fas fa-heart-circle-plus absolute xs:top-3 xs:right-3 top-1 right-1 text-3xl"
  //         style={{ color: '#ff4e4e' }}
  //       />
  //     )
  //   }
  // }

  const leftColumnsClasses =
    partner === 'regiondo' ? 'w-full md:w-1/2' : 'md:flex-grow'
  const rightColumnClasses =
    partner === 'regiondo'
      ? 'w-full md:w-1/2'
      : 'w-full md:w-96 md:max-w-[384px] md:flex-shrink-0'
  return (
    <>
      <PageHead
        meta_title={meta_title}
        meta_description={meta_description}
        alternates={getAlternates(product)}
        wildcards={{ h1: product.h1 }}
        item={product}
        coverImage={image}
      />
      <div className="flex flex-col flex-wrap justify-start items-stretch mt-2">
        {/* Mobile-only location and reviews section above image */}
        <div className="xs:hidden flex justify-between items-center mx-3 text-xs font-regular text-neutral">
          <span>
            {locationName}, {nameCountry}
          </span>
          <Reviews
            stars={stars}
            numberOfReviews={numberOfReviews}
            productLink={product_link}
            name={h1}
            isMobileAboveImage={true}
          />
        </div>

        <div className="xs:h-104 h-80 rounded-lg overflow-hidden mx-3 xs:mx-0 mt-2 relative">
          <IdealImage
            title={replaceWildcards(h1 + ' - SIGHT2SIGHT')}
            alt={replaceWildcards(h1 + ' - SIGHT2SIGHT')}
            priority={true}
            image={image}
            width={820}
            height={550}
          />
          <div
            className="cursor-pointer absolute z-40 h-12 w-12 top-2 right-2 flex items-center justify-center"
            // onClick={() =>
            //   isBookmarked ? removeBookmark(_id) : addBookmark(_id)
            // }
            // onMouseEnter={() => setIsHovered(true)}
            // onMouseLeave={() => setIsHovered(false)}
          >
            {/* {renderBookmarkIcon()} */}
          </div>
          <div
            className="absolute bottom-0 right-0 h-6 w-6"
            onClick={() => navigator.clipboard.writeText(product._id)}
          />
        </div>
        <div className="mx-3 xs:mx-0 md:mt-6 mt-3 mb-2 gap-1 flex flex-col md:items-center items-start justify-start">
          <h1 className="w-full md:text-3xl text-xl font-medium uppercase text-center">
            {h1}
          </h1>
          {name_description && (
            <h2 className="md:text-[15px] text-[13px] font-medium uppercase text-center">
              {name_description?.replace(/\.$/, '')}
            </h2>
          )}
          <div className="hidden xs:flex flex-shrink-0 text-sm text-neutral items-center whitespace-nowrap">
            <span>
              {locationName}, {nameCountry}
            </span>
          </div>
          <div className="hidden xs:flex justify-between items-center">
            <div className="flex-1 min-w-0">
              <Reviews
                stars={stars}
                numberOfReviews={numberOfReviews}
                productLink={product_link}
                name={h1}
              />
            </div>
          </div>
          <div className="flex md:self-center self-stretch mt-4 mb-1">
            <div className="md:min-w-96 w-full flex-grow">
              <BookButton product={product} />
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mx-3 xs:mx-0 mt-4">
          <div className={`${leftColumnsClasses} flex flex-col gap-4`}>
            <ProductDetails product={product} />
          </div>
          <div className={`${rightColumnClasses}`} id="check-availability">
            <BookProduct
              price={price}
              productLink={product_link}
              bookingEngine={booking_engine}
              productName={h1}
              enginePartner={partner}
              productId={partner_product_id}
            />
          </div>
        </div>
      </div>
      {highlights && (
        <Highlights
          highlights={highlights}
          wildcards={{ location_name: location.name, h1 }}
        />
      )}
      {route?.images && route.images.length > 0 && (
        <Route route={route} h1={h1} />
      )}
      {description_2 && !!description_2.length && (
        <Description description={description_2} withBorder />
      )}
      {description_1 && !!description_1.length && (
        <Description description={description_1} withBorder noTopMargin />
      )}
      <div className="flex justify-center mx-3 mt-6">
        <BookButton product={product} />
      </div>
      {description_3 && !!description_3.length && (
        <Description description={description_3} withBorder />
      )}
      {category && category.length > 0 && (
        <TileSlider
          labelKey={'similar-products-in-location'}
          location={locationName}
          loadWith={getGetSimilarProducts(
            category.map(({ _id }) => _id),
            _id,
          )}
          type={'product'}
        />
      )}
      {locationProductLink && (
        <Widget
          product_link={locationProductLink}
          coverImage={locationCoverImage}
          name={locationName}
        />
      )}
      <TileSlider
        loadWith={getGetAllCategoriesInLocation(locationId)}
        labelKey="categories-in-location"
        location={locationName}
        type={'category'}
      />
    </>
  )
}
